/**
 * Automation Block Type Definitions
 *
 * Types for configuring automation inputs, processes, and outputs.
 */

// =============================================================================
// Constants (E018-S004-T002)
// =============================================================================

// sl-za0v.12.3: EXTERNAL_SOURCE_ID removed. Use triggerConfig.type === 'webhook' instead.

// =============================================================================
// Filter Types - Re-export from Data-View
// =============================================================================

// Use Data-View's filter system for consistency
import type { FilterGroup as _FilterGroup } from '@blocksuite/affine/data-view';
import {
  AUTOMATION_BLOCK_BATCH_DEFAULTS,
  AUTOMATION_BLOCK_STATUS_TRACKING_DEFAULTS,
  AUTOMATION_BLOCK_TRIGGER_DEFAULTS,
  type ProcessingTrackingMode,
  type TriggerType,
} from '@streamlain/automation-safety-contract';

export type {
  Filter,
  FilterGroup,
  SingleFilter,
} from '@blocksuite/affine/data-view';

/** Trigger types (incl. the row triggers); single source: automation contract. */
export type { TriggerType } from '@streamlain/automation-safety-contract';
// Local alias for use within this file
type FilterGroup = _FilterGroup;

// Legacy type alias for migration - will be removed
export interface FilterConfig {
  id: string;
  columnId: string;
  operator: string;
  value: unknown;
  logicalOp?: 'AND' | 'OR';
}

// =============================================================================
// Mapping Types
// =============================================================================

export type TransformType =
  | 'none'
  | 'uppercase'
  | 'lowercase'
  | 'trim'
  | 'extract_email_domain';

export interface MappingConfig {
  id: string;
  sourceColumnId: string;
  targetColumnId: string;
  transform?: TransformType;
}

// =============================================================================
// Input Types (sl-m379: Multi-Input Tables)
// =============================================================================

/**
 * sl-m379: An input table source for the automation.
 * Exactly one table has role 'lead' (iterated for execution).
 * Additional tables with role 'secondary' must be linked through a lead-table relation.
 */
export interface InputTable {
  /** Client-generated stable ID (nanoid) */
  id: string;
  /** Block ID of the source table */
  tableId: string;
  /** Display name of the table */
  tableName: string;
  /** Doc ID where the table resides (for CrossDoc support) */
  docId?: string;
  /** Role: 'lead' = iterated + status tracked, 'secondary' = relation lookup only */
  role: 'lead' | 'secondary';
  /** Data-View compatible filter group on the lead table; ignored on relations */
  filterGroup?: FilterGroup;
  /**
   * Required for secondary inputs: relation column ID on the lead table.
   * Optional in persisted types so unsupported older drafts can be shown and removed.
   */
  linkedVia?: string;
  /**
   * sl-i6t9: Relation cardinality for linked secondaries.
   * - 'one': at most one linked row, grouped as a zero-or-one-element array
   * - 'many': multiple linked rows, grouped into array field in composite
   */
  cardinality?: 'one' | 'many';
  /**
   * sl-i6t9: Explicit array field name for many-cardinality linked tables.
   * When set, the multi-relation array uses this key in the composite payload.
   * When unset, auto-generated from tableName (lowercased, spaces→underscores).
   */
  multiFieldName?: string;
}

/**
 * sl-i6t9 (A4): Maximum auto-detected relation tables shown in the UI.
 * Shared between FE (display cap) and referenced in BE contract.
 * Backend enforces its own per-row link limit (APP_BASE_RELATION_LINK_LIMIT = 100, @streamlain/view-config).
 */
export const MAX_RELATION_TABLES_DISPLAY = 20;

// =============================================================================
// Output Types
// =============================================================================

export type OutputMode = 'update_source' | 'append';

/**
 * Multi-Output: A target table for writing automation results.
 * Multiple tables can be configured for fan-out scenarios.
 */
export interface OutputTable {
  /** Block ID of the target table */
  tableId: string;
  /** Display name of the table */
  tableName: string;
  /** Doc ID where the table resides (for CrossDoc support) */
  docId: string;
  /** sl-mau5: Reference to MatchConfigProps entry for row matching */
  matchConfigId?: string;
}

/**
 * Multi-Output: Mapping from response field to target column.
 * Each mapping specifies which table and column to write to.
 *
 * sl-mau5: Match config is now a first-class object in matchConfigs[].
 * OutputMapping only stores the reference via matchConfigId.
 */
export interface OutputMapping {
  /** Which output table this mapping writes to (from outputTables) */
  tableId: string;
  /** Response field path (e.g., "order.id", "invoice.total") */
  responseField: string;
  /** Target column ID in the table */
  targetColumnId: string;
  /** sl-mau5: Reference to MatchConfigProps entry for relation lookup */
  matchConfigId?: string;
}

/**
 * sl-mau5: First-class match configuration for row matching and relation lookup.
 *
 * Replaces 7 scattered fields that were on OutputMapping.
 * Referenced by OutputTable.matchConfigId (row matching) and
 * OutputMapping.matchConfigId (relation lookup).
 */
export type MultiMatchBehavior =
  | 'fail'
  | 'update_all'
  | 'update_first'
  | 'skip'
  | 'ask';

export interface MatchConfigProps {
  /** Client-generated stable ID (nanoid) */
  id: string;
  /** Target table ID to search in */
  targetTableId: string;
  /** Behavior when multiple rows match */
  multiMatchBehavior: MultiMatchBehavior;
  /** Behavior when no match found (only for row matching, not relation lookup) */
  notFoundBehavior?: 'fail' | 'create';
  /** sl-nhma: Behavior when inverse relation steal conflict occurs */
  stealBehavior?: 'fail' | 'silent_steal' | 'ask';
  /** Match keys: which columns to search and how */
  keys: Array<{
    /** Column ID in the target table */
    columnId: string;
    /** Field from webhook response to use as search value */
    sourceField: string;
    /** How to match: auto | exact | contains | starts_with */
    matchMode: string;
  }>;
}

// =============================================================================
// Runtime Types
// =============================================================================

export type RunStatus = 'success' | 'error' | 'running' | 'cancelled' | null;

// sl-1ahs.6.1: ValidationStatus / ValidationState / ConfigSnapshot /
// DEFAULT_VALIDATION_STATE removed together with the Test Run infrastructure
// (sl-1ahs.7.2). Validation is now driven by the workflow End-node output
// schema; see IAutomationConfigToolAdapter.getWorkflowOutputSchema (7.3).

// =============================================================================
// Batching & Error Handling Types (T037)
// =============================================================================

/**
 * What to do when a row/batch fails after all retries
 * - continue: Mark as failed, proceed with next row/batch
 * - pause: Pause execution, user must click "Resume"
 * - stop: Abort entire execution
 */
export type OnFailureMode = 'continue' | 'pause' | 'stop';

/**
 * Retry configuration with exponential backoff
 */
export interface RetryConfig {
  enabled: boolean;
  maxAttempts: number; // e.g., 3
  initialDelayMs: number; // e.g., 1000
  backoffMultiplier: number; // e.g., 2
  maxDelayMs: number; // e.g., 30000
}

/**
 * Batching configuration for controlled row processing (T037, T047)
 */
export interface BatchConfig {
  /**
   * Number of rows to process per run (T047)
   * - 1-100: Process this many rows
   * - Default: 1
   */
  batchSize: number;
  /**
   * Whether to send all rows as a single array to the webhook (T047)
   * - true: 1 webhook call with all rows as array
   * - false: N webhook calls, one per row
   * - Default: false
   */
  sendAsArray: boolean;
  /** Retry settings for failed API calls */
  retry: RetryConfig;
  /** What to do when a batch fails */
  onFailure: OnFailureMode;
}

/**
 * Default batch config (T047)
 * - batchSize: 1 (process one row at a time)
 * - sendAsArray: false (send rows individually)
 */
export const DEFAULT_BATCH_CONFIG: BatchConfig =
  AUTOMATION_BLOCK_BATCH_DEFAULTS;

// =============================================================================
// Trigger Types (T036)
// =============================================================================

/**
 * Trigger type - WHEN does the automation run?
 * - manual: User clicks "Execute" button
 * - scheduled: Cron-based schedule (e.g., every 5 minutes, daily at 9am)
 * - webhook: Triggered by external HTTP request via inbound webhook
 */
/**
 * Trigger status - is the trigger active?
 */
export type TriggerStatus = 'active' | 'paused' | 'inactive';

/**
 * Common cron presets for easy selection
 */
export type CronPreset =
  | 'nightly'
  | 'every_1_min'
  | 'every_30_min'
  | 'hourly'
  | 'daily_6pm'
  | 'weekly_monday_9am'
  | 'monthly_first_9am'
  | 'every_5_min'
  | 'every_15_min'
  | 'every_hour'
  | 'daily_9am'
  | 'weekly_monday'
  | 'custom';

/**
 * Cron preset to expression mapping
 */
export const CRON_PRESETS: Record<
  Exclude<CronPreset, 'custom' | 'nightly'>,
  { label: string; expression: string }
> = {
  every_1_min: { label: 'Every minute', expression: '*/1 * * * *' },
  every_30_min: { label: 'Every 30 minutes', expression: '*/30 * * * *' },
  hourly: { label: 'Every hour', expression: '0 * * * *' },
  daily_6pm: { label: 'Daily at 6:00 PM', expression: '0 18 * * *' },
  weekly_monday_9am: { label: 'Weekly on Monday', expression: '0 9 * * 1' },
  monthly_first_9am: { label: 'Monthly on the first', expression: '0 9 1 * *' },
  every_5_min: { label: 'Every 5 minutes', expression: '*/5 * * * *' },
  every_15_min: { label: 'Every 15 minutes', expression: '*/15 * * * *' },
  every_hour: { label: 'Every hour', expression: '0 * * * *' },
  daily_9am: { label: 'Daily at 9:00 AM', expression: '0 9 * * *' },
  weekly_monday: { label: 'Weekly on Monday', expression: '0 9 * * 1' },
};

/**
 * Trigger configuration
 */
export interface TriggerConfig {
  /** Trigger type */
  type: TriggerType;

  /** Whether the trigger is enabled */
  enabled: boolean;

  // ---- Scheduled trigger options ----
  /** Cron preset for easy selection */
  cronPreset?: CronPreset;
  /** Custom cron expression (used when cronPreset is 'custom') */
  cronExpression?: string;
  /** Timezone for scheduled triggers (default: browser timezone) */
  timezone?: string;

  // ---- Debounce options ----
  /** Debounce delay in ms (optional, e.g. for webhook dedup) */
  debounceMs?: number;

  // ---- Runtime state (not persisted) ----
  /** Next scheduled run timestamp (calculated, not persisted) */
  nextRunAt?: number;
}

/**
 * Default trigger config (manual, inactive)
 */
export const DEFAULT_TRIGGER_CONFIG: TriggerConfig =
  AUTOMATION_BLOCK_TRIGGER_DEFAULTS;

// =============================================================================
// Status Tracking Types (T044, E018-S004-T001)
// =============================================================================

/**
 * Processing tracking mode - HOW to track processed rows
 * - status_column: Update a Select column with success/error values (original)
 * - delete_row: Delete the input row after successful processing (E018-S004-T001)
 *
 * These modes are mutually exclusive.
 */
export type { ProcessingTrackingMode } from '@streamlain/automation-safety-contract';

/**
 * Standard status values for select column options
 */
export const STATUS_STANDARD_VALUES = {
  success: ['Done', 'Processed', 'Completed'],
  error: ['Error', 'Failed'],
} as const;

/**
 * Status tracking configuration for post-processing
 *
 * After successful automation run, either:
 * - Updates a Select column in the source table (mode: 'status_column')
 * - Deletes the input row from the source table (mode: 'delete_row')
 *
 * Key features:
 * - Modes are mutually exclusive
 * - 'status_column': Uses Select columns for tracking, System-Filter skips processed rows
 * - 'delete_row': Permanently removes rows after successful processing (E018-S004-T001)
 */
export interface StatusTrackingConfig {
  /** Whether status tracking is enabled */
  enabled: boolean;

  /**
   * Processing mode (E018-S004-T001)
   * - 'status_column': Update a Select column (original behavior)
   * - 'delete_row': Delete input row after successful processing
   *
   * Default: 'status_column' for backwards compatibility
   */
  mode: ProcessingTrackingMode;

  /**
   * Column ID to update (only used when mode === 'status_column')
   * - null = create new column (use createColumn config)
   * - string = use existing select column
   */
  columnId: string | null;

  /**
   * Configuration for creating a new column (when columnId is null)
   * Only used when mode === 'status_column'
   */
  createColumn?: {
    /** Column name (e.g., "Status", "Processing Status") */
    name: string;
  };

  /**
   * Standard options to add to the select column
   * Only used when mode === 'status_column'
   */
  standardOptions: string[];

  /**
   * Value a run sets on the rows it takes, before the workflow starts.
   * Required for automatic triggers over a table.
   * Only used when mode === 'status_column'
   */
  pendingValue?: string;

  /**
   * Value to set on successful processing
   * Only used when mode === 'status_column'
   */
  successValue: string;

  /**
   * Value to set on error (optional)
   * Only used when mode === 'status_column'
   * If not set, rows with errors keep their current value
   */
  errorValue?: string;
}

/**
 * Default status tracking config (disabled, status_column mode)
 */
export const DEFAULT_STATUS_TRACKING_CONFIG: StatusTrackingConfig =
  AUTOMATION_BLOCK_STATUS_TRACKING_DEFAULTS;
